import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Package } from "lucide-react";
import { api } from "@/lib/api";
import { ShinyButton } from "@/components/ui/ShinyButton";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function CategoryGrid() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);

  useEffect(() => {
    api.get("/shop/categories")
      .then(({ data }) => {
        const list = data.categories || data || [];
        setCategories(list);
      })
      .catch(() => setCategories([]));
  }, []);

  if (!categories || categories.length === 0) return null;

  return (
    <section id="shop-by-category" data-testid="category-grid-section" className="relative py-20 lg:py-24">
      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
              <span className="h-px w-10 bg-amber-500" />
              Browse The Range
            </p>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl" data-testid="category-grid-title">
              Shop by Category
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-2 sm:text-base">
              Every packaging essential your business needs — organised and one click away.
            </p>
          </div>
          <ShinyButton
            data-testid="category-view-all-btn"
            onClick={() => navigate("/shop")}
            className="transition-transform duration-300 hover:scale-[1.03]"
            contentClassName="px-6 py-3 text-sm font-bold"
          >
            View All Products
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </ShinyButton>
        </motion.div>

        <div
          className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-6"
          data-testid="category-grid"
        >
          {categories.map((c, i) => (
            <motion.button
              key={c.id}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.06 }}
              onClick={() => navigate(`/shop?category=${encodeURIComponent(c.name)}`)}
              data-testid={`category-card-${c.slug}`}
              className="group flex flex-col items-center text-center"
            >
              <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-line bg-white shadow-card transition-all duration-300 group-hover:-translate-y-1 group-hover:border-amber-500/50 group-hover:shadow-lift">
                {c.image ? (
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-surface" />
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_70%,rgba(255,159,28,0.08),transparent_65%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Package className="h-10 w-10 text-3 transition-colors duration-300 group-hover:text-amber-500" strokeWidth={1.4} />
                    </span>
                  </>
                )}
              </div>
              <p className="mt-3 text-sm font-bold text-1 transition-colors group-hover:text-amber-500">{c.name}</p>
              <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-3">
                {c.count} product{c.count === 1 ? "" : "s"}
                <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
              </p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
